import { ReactElement } from "react";
import { Price } from "src/components";
import { PandaDocRole } from "src/generated/graphqlTypes";
import { LienWaiverPdfQuery } from "src/routes/pdfs/LienWaiverPdf/LienWaiverPdf.generated";
import { TextField } from "../components/PandaDocField";

export type LienWaiverPdf = LienWaiverPdfQuery["lienWaiver"];

export const invoiceNumber = ({ parent }: LienWaiverPdf): ReactElement => {
  return <span data-testid="invoiceNumber">{"invoiceNumber" in parent ? parent.invoiceNumber : ""}</span>;
};

export const throughDate = ({ throughDate }: LienWaiverPdf): ReactElement => {
  return <span>{throughDate?.toLocaleDateString() ?? ""}</span>;
};

export const amountInCents = ({ amountInCents }: LienWaiverPdf): ReactElement => {
  return (
    <span>
      <Price valueInCents={amountInCents ?? 0} />
    </span>
  );
};

// NOTE: Intentionally not using `payerName` here so that we return the same value
// when the payer is of type Builder (i.e. "Homebound Technologies, Inc.").
export const payerName = ({ payer }: LienWaiverPdf, dataId: string): ReactElement => {
  return <span data-testid={dataId}>{payer.name}</span>;
};

export const customerName = ({ payerName }: LienWaiverPdf, dataId: string): ReactElement => {
  return <span data-testid={dataId}>{payerName}</span>;
};

export const claimantName = ({ claimant }: LienWaiverPdf, dataId: string): ReactElement => {
  return <span data-testid={dataId}>{claimant.name}</span>;
};

export const buildAddress = (lienWaiver: LienWaiverPdf): ReactElement => {
  const buildAddress = lienWaiver.project.buildAddress!; // any project with a lien waiver must have a build address
  return (
    <span data-testid="buildAddress">{`${buildAddress.street1}, ${buildAddress.city}, ${buildAddress.state} ${buildAddress.postalCode}`}</span>
  );
};

export const owner = (lienWaiver: LienWaiverPdf, defaultName: string) => {
  const name =
    lienWaiver.project.primaryHomeowner?.fullName ?? lienWaiver.project.customerOwnerEntity?.name ?? defaultName;
  return <span data-testid="owner">{name}</span>;
};

export const getSignatoryName = (lienWaiver: LienWaiverPdf) => (
  <span data-testid="tpSignatoryName">{lienWaiver.signatory?.name}</span>
);

export const getTitleField = (prefix?: string) => <TextField prefix={prefix} role={PandaDocRole.Signatory} />;

export const getClaimantName = (prefix?: string) => <TextField prefix={prefix} role={PandaDocRole.Signatory} />;

export const getExceptionsField = (prefix?: string) => <TextField prefix={prefix} role={PandaDocRole.Signatory} />;
